import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { FiAward, FiCalendar, FiUpload, FiUser, FiFileText } from 'react-icons/fi';
import apiClient, { endpoints } from '../api/client';

const DeleteItemModal = ({ entity, url, isOpen, onClose, onItemDeleted }) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { showSuccess, showError } = useToast();

    const capitalize = (str) => {
        if (!str) return str; // guard against empty/null
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);

        try {
            const response = await apiClient.delete(url);

            if (response.data.success) {
                showSuccess(`${capitalize(entity)} deleted successfully`);
                onItemDeleted(response.data.data);
                handleClose();
            } else {
                showError(response.data.message || `Failed to delete ${entity}`);
            }
        } catch (error) {
            // Comprehensive error logging for debugging
            console.error(`Error deleting ${entity}:`, error);

            // Log detailed AxiosError information
            if (error.isAxiosError) {
                console.error('Axios Error Details:', {
                    message: error.message,
                    name: error.name,
                    code: error.code,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.response?.headers,
                    config: {
                        url: error.config?.url,
                        method: error.config?.method,
                        baseURL: error.config?.baseURL,
                        headers: error.config?.headers,
                        timeout: error.config?.timeout
                    }
                });

                // Log full error stack trace
                console.error('Error Stack Trace:', error.stack);
            }

            // Determine appropriate error message based on status code
            let errorMessage = `Failed to delete ${entity}`;

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message;

                switch (status) {
                    case 400:
                        errorMessage = serverMessage || `Invalid ${entity} data provided. Please check all required fields.`;
                        break;
                    case 401:
                        errorMessage = `Authentication required. Please log in again.`;
                        break;
                    case 403:
                        errorMessage = `You do not have permission to delete this ${entity}.`;
                        break;
                    case 404:
                        errorMessage = `${capitalize(entity)} delete endpoint not found.`;
                        break;
                    case 413:
                        errorMessage = `${capitalize(entity)} file is too large. Please choose a smaller file.`;
                        break;
                    case 422:
                        errorMessage = serverMessage || `Invalid ${entity} data format.`;
                        break;
                    case 500:
                        errorMessage = `Server error occurred. Please try again later.`;
                        break;
                    default:
                        errorMessage = serverMessage || `Request failed with status ${status}`;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
                console.error('Network Error - No response received:', error.request);
            } else {
                errorMessage = `An unexpected error occurred while deleting the ${entity}.`;
                console.error('Request Setup Error:', error.message);
            }

            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={`Delete ${capitalize(entity)}`} size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                        Are you sure you want to delete this {entity}?
                    </p>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FiAward className="-ml-1 mr-2 h-4 w-4 inline" />
                                Delete {capitalize(entity)}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default DeleteItemModal;